import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField, CircularProgress } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import { useAuth } from "hooks/useAuth";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import AppSwitch from "components/Shared/AppSwitch";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

// Utility function for current time
const getDateTimeAuto = () => new Date().toISOString().slice(0, 16);

const INITIAL_STATE = {
  account: null,
  entity: null,
  vehicleId: null,
  driverId: null,
  incidentId: "",
  incidentType: null,
  description: "",
  status: null,
  isActive: true,
  incidentDate: getDateTimeAuto(),
};

const IncidentReportForm = ({
  isBusy = false,
  incidentReportDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();

  const {
    data: entityTypeList = [],
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
  } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.INCIDENT_STATUS,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );
  const {
    data: entityIncidentType = [],
    isLoading: isLoadingIncidentTypes,
    isFetching: isFetchingIncidentTypes,
  } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.INCIDENT_TYPES,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );
  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
    },
    mode: "onChange",
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchDriver = useWatch({ control, name: "driverId" });

  // Memoize mapped lists
  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
            label: item?.name || "",
            _id: item?._id || "",
          }))
        : [],
    [entityTypeList]
  );

  const _mappedIncidentType = useMemo(
    () =>
      Array.isArray(entityIncidentType)
        ? entityIncidentType.map((item) => ({
            label: item?.name || "",
            _id: item?._id || "",
          }))
        : [],
    [entityIncidentType]
  );

  // Derive vehicles from selected driver's assigned_vehicle_ids
  const selectedVehicles = useMemo(
    () => _watchDriver?.assigned_vehicle_ids || [],
    [_watchDriver]
  );

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      vehicleId: data?.vehicleId?._id,
      driverId: data?.driverId?._id,
      incidentType: data?.incidentType?._id,
      status: data?.status?._id,
    };
    onSubmit(formattedData);
  };

  // Reset form when incidentReportDetail changes
  const isMounted = useRef(true);
  useEffect(() => {
    if (
      !isMounted.current ||
      isLoadingStatus ||
      isFetchingStatus ||
      isLoadingIncidentTypes ||
      isFetchingIncidentTypes
    )
      return;

    if (!incidentReportDetail) {
      reset({
        ...INITIAL_STATE,
        entity: viewEntity?.entity || null,
        account: viewEntity?.entity?.account || null,
      });
      return;
    }

    const driverObj = drivers
      ? Object.values(drivers).find(
          (d) => d._id === incidentReportDetail.driverId?._id
        ) || incidentReportDetail.driverId
      : incidentReportDetail.driverId;

    const vehicleObj = driverObj?.assigned_vehicle_ids
      ? driverObj.assigned_vehicle_ids.find(
          (v) => v._id === incidentReportDetail.vehicleId?._id
        ) || incidentReportDetail.vehicleId
      : incidentReportDetail.vehicleId;

    const incidentType = entityIncidentType.find(
      (type) =>
        type._id ===
        (incidentReportDetail.incidentType?._id ||
          incidentReportDetail.incidentType)
    );

    const status = entityTypeList.find(
      (status) =>
        status._id ===
        (incidentReportDetail.status?._id || incidentReportDetail.status)
    );

    reset({
      ...INITIAL_STATE,
      ...incidentReportDetail,
      account: incidentReportDetail.account || null,
      entity: incidentReportDetail.entity || null,
      driverId: driverObj || null,
      vehicleId: vehicleObj || null,
      incidentType: incidentType
        ? { _id: incidentType._id, label: incidentType.name }
        : null,
      status: status ? { _id: status._id, label: status.name } : null,
      incidentDate: incidentReportDetail.incidentDate
        ? new Date(incidentReportDetail.incidentDate).toISOString().slice(0, 16)
        : getDateTimeAuto(),
    });

    return () => {
      isMounted.current = false;
    };
  }, [
    incidentReportDetail,
    reset,
    drivers,
    viewEntity,
    entityTypeList,
    entityIncidentType,
    isLoadingStatus,
    isFetchingStatus,
    isLoadingIncidentTypes,
    isFetchingIncidentTypes,
  ]);

  // Fetch entities when account changes
  useEffect(() => {
    if (
      (isAccountAdmin || isSystemAdmin) &&
      _watchAccount?._id &&
      isMounted.current
    ) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch drivers when account or entity changes
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id && isMounted.current) {
      fetchDrivers({ account: _watchAccount._id, entity: _watchEntity._id });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchDrivers]);

  // Fetch drivers for editing
  useEffect(() => {
    if (
      incidentReportDetail?.account?._id &&
      incidentReportDetail?.entity?._id &&
      isMounted.current
    ) {
      fetchDrivers({
        account: incidentReportDetail.account._id,
        entity: incidentReportDetail.entity._id,
      });
    }
  }, [incidentReportDetail, fetchDrivers]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("driverId", null);
                      setValue("vehicleId", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("driverId", null);
                      setValue("vehicleId", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          {/* Driver ID */}
          <Grid item md={6}>
            <Controller
              name="driverId"
              control={control}
              rules={{ required: "Please select Driver ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Driver ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("vehicleId", "");
                  }}
                  getOptionLabel={(option) =>
                    option ? `${option?.driverId} - ${option?.name}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={Object.values(drivers || {})}
                  loading={loadingDrivers}
                  loadingText="Loading Drivers..."
                  helperText={errors?.driverId?.message}
                  error={!!errors?.driverId}
                />
              )}
            />
          </Grid>

          {/* Vehicle ID */}
          <Grid item md={6}>
            <Controller
              name="vehicleId"
              control={control}
              rules={{ required: "Please select Vehicle ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle"
                  required
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option && option.vehicle_id
                      ? `${option.vehicle_id}${
                          option.registration_number
                            ? ` - ${option.registration_number}`
                            : ""
                        }`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={selectedVehicles}
                  disabled={!_watchDriver}
                  loadingText="Loading Vehicles..."
                  helperText={
                    errors?.vehicleId?.message ||
                    (!_watchDriver
                      ? "Please select a driver first"
                      : selectedVehicles.length === 0
                      ? "No vehicles assigned to this driver"
                      : "")
                  }
                  error={!!errors?.vehicleId}
                />
              )}
            />
          </Grid>
          {/* Incident Type */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="incidentType"
              control={control}
              rules={{ required: "Incident Type is required." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Incident Type"
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={_mappedIncidentType}
                  loading={isLoadingIncidentTypes || isFetchingIncidentTypes}
                  loadingText="Loading Incident Types..."
                  disabled={isLoadingIncidentTypes || isFetchingIncidentTypes}
                  helperText={errors?.incidentType?.message}
                  error={!!errors?.incidentType}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Incident Type"
                      required
                      error={!!errors?.incidentType}
                      helperText={errors?.incidentType?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(isLoadingIncidentTypes ||
                              isFetchingIncidentTypes) && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {/* Incident Date */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="incidentDate"
              control={control}
              rules={{ required: "Incident Date is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="datetime-local"
                  label="Incident Date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={field.value || getDateTimeAuto()}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!errors.incidentDate}
                  helperText={errors.incidentDate?.message}
                />
              )}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Status is required." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Status"
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={_mappedAccountTypeList}
                  loading={isLoadingStatus || isFetchingStatus}
                  loadingText="Loading Status Options..."
                  disabled={isLoadingStatus || isFetchingStatus}
                  helperText={errors?.status?.message}
                  error={!!errors?.status}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      error={!!errors?.status}
                      helperText={errors?.status?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(isLoadingStatus || isFetchingStatus) && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
          {incidentReportDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}

          {/* Description */}
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Description"
                  size="small"
                  multiline
                  rows={2}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={
            isBusy ||
            isLoadingStatus ||
            isFetchingStatus ||
            isLoadingIncidentTypes ||
            isFetchingIncidentTypes
          }
        >
          {incidentReportDetail ? "Update Incident" : "Add Incident"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default IncidentReportForm;
